import { GridPaginationModel, GridSortItem, GridSortModel } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'src/hooks/use-debounce';
import { makeBenefitsApi } from 'src/http/api/internal/internal-http';
import { BenefitsRepository } from 'src/repositories/benefits/benefits.repository';
import { Benefits } from 'src/types/benefits';
import {
  BenefitsDrawerApplyPayload,
  BenefitsDrawerFilterDraft,
  BenefitsMappedFilters,
} from '../../benefits-list/components/benefits-drawer-filters.hook';
import { getBenefitsColumns } from '../../benefits-list/components/benefits-list-columns';

type BenefitStatus = 'active' | 'inactive';

type UseRelatedBenefitsListParams = {
  entityId: string;
  entityLabel: 'tema' | 'categoria';
  fixedFilterField: 'themesId' | 'categoriesId';
};

type BenefitsListResponse = {
  data: Benefits[];
  page: number;
  pageSize: number;
  total: number;
  totalFiltered: number;
  totalPages: number;
};

const EMPTY_LIST: BenefitsListResponse = {
  data: [],
  page: 0,
  pageSize: 10,
  total: 0,
  totalFiltered: 0,
  totalPages: 0,
};

const repository = new BenefitsRepository(makeBenefitsApi());

const getBenefitImage = (image: unknown): string => {
  if (typeof image === 'string') {
    return image;
  }

  if (Array.isArray(image)) {
    const firstImage = image[0];

    if (typeof firstImage === 'string') {
      return firstImage;
    }

    if (firstImage && typeof firstImage === 'object') {
      const imageData = firstImage as { urlName?: unknown; url?: unknown };
      return String(imageData.urlName || imageData.url || '');
    }
  }

  if (image && typeof image === 'object') {
    const imageData = image as { urlName?: unknown; url?: unknown };
    return String(imageData.urlName || imageData.url || '');
  }

  return '';
};

const mapSortModelToRequest = (sort: GridSortItem | null) => {
  if (!sort) {
    return null;
  }

  return {
    ...sort,
    field: sort.field === 'pointsValue' ? 'price' : sort.field,
  };
};

export default function useThemeBenefitsList({
  entityId,
  entityLabel,
  fixedFilterField,
}: UseRelatedBenefitsListParams) {
  const { enqueueSnackbar } = useSnackbar();
  const entityPrepositionalLabel = entityLabel === 'tema' ? 'do tema' : 'da categoria';
  const [benefitsList, setBenefitsList] = useState<BenefitsListResponse>(EMPTY_LIST);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchTextState] = useState('');
  const debouncedSearchText = useDebounce(searchText, 500);
  const [filters, setFilters] = useState<BenefitsMappedFilters>([]);
  const [appliedDraftFilters, setAppliedDraftFilters] = useState<BenefitsDrawerFilterDraft | null>(
    null
  );
  const [sortModel, setSortModel] = useState<GridSortItem | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [openFiltersDrawer, setOpenFiltersDrawer] = useState(false);
  const [statusByBenefitId, setStatusByBenefitId] = useState<Record<string, BenefitStatus>>({});
  const latestRequestRef = useRef(0);

  const requestFilters = useMemo<BenefitsMappedFilters>(
    () => [
      ...filters.filter((filter) => filter.field !== fixedFilterField),
      { field: fixedFilterField, value: [entityId] },
    ],
    [entityId, filters, fixedFilterField]
  );

  const requestSortModel = useMemo(() => mapSortModelToRequest(sortModel), [sortModel]);

  useEffect(() => {
    if (!entityId) {
      return;
    }

    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setIsLoading(true);

    const getBenefits = async () => {
      try {
        const response = await repository.getBenefitsList({
          search: debouncedSearchText,
          filters: JSON.stringify(requestFilters),
          sort: JSON.stringify(requestSortModel),
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
        });

        if (requestId !== latestRequestRef.current) {
          return;
        }

        if (response.isLeft()) {
          setBenefitsList(EMPTY_LIST);
          enqueueSnackbar(
            response.value.message || `Erro ao carregar os benefícios ${entityPrepositionalLabel}.`,
            { variant: 'error' }
          );
          return;
        }

        setBenefitsList({ ...EMPTY_LIST, ...response.value });
      } catch {
        if (requestId === latestRequestRef.current) {
          setBenefitsList(EMPTY_LIST);
          enqueueSnackbar(`Erro ao carregar os benefícios ${entityPrepositionalLabel}.`, {
            variant: 'error',
          });
        }
      } finally {
        if (requestId === latestRequestRef.current) {
          setIsLoading(false);
        }
      }
    };

    getBenefits();
  }, [
    debouncedSearchText,
    entityId,
    entityPrepositionalLabel,
    enqueueSnackbar,
    paginationModel.page,
    paginationModel.pageSize,
    requestFilters,
    requestSortModel,
  ]);

  const formatDate = useCallback(
    (value: string) => new Date(value).toLocaleDateString('pt-BR', { timeZone: 'UTC' }),
    []
  );
  const formatPoints = useCallback((points: number) => `${points.toLocaleString('pt-BR')} pts`, []);

  const rows = useMemo(
    () =>
      benefitsList.data.map((item: Benefits & { id?: string; _id?: string }, index) => {
        const benefitId = String(item.id || item._id || (item as any)?.benefitId || '');
        const exhibitionEnv = String(item.exhibitionEnv || '')
          .trim()
          .toLowerCase();
        const isParking = exhibitionEnv === 'parking' || exhibitionEnv === 'estacionamento';

        return {
          benefitId,
          benefitType: item.benefitType?.toLowerCase() === 'coupon' ? 'coupon' : 'product',
          image: getBenefitImage((item as { image?: unknown }).image),
          id:
            item.id ||
            item._id ||
            `${benefitsList.page}-${index}-${item.name}-${String(item.startDate)}`,
          name: item.name,
          company: isParking ? 'Sem empresa associada' : item.entityName,
          exhibitionEnv: item.exhibitionEnv,
          isParking,
          pointsValue: item.price,
          type: item.benefitType?.toLowerCase() === 'coupon' ? 'Cupom' : 'Produto',
          totalQuantity: item.totalQuantity,
          availableQuantity: item.availableQuantity,
          redeemedQuantity: item.redeemedQuantity,
          withdrawnQuantity: item.usedQuantity,
          createdAt: item.startDate ? String(item.startDate) : '',
          startDate: item.startDate ? String(item.startDate) : null,
          endDate: item.endDate ? String(item.endDate) : null,
          tier: item.tier,
          visibleForTiers: item.visibleForTiers,
          status: statusByBenefitId[benefitId] || item.status,
        };
      }),
    [benefitsList, statusByBenefitId]
  );

  const tierColumnWidth = useMemo(() => {
    const maxTierCount = rows.reduce((maxCount, row) => {
      const rowTiers = Array.isArray(row.visibleForTiers) ? row.visibleForTiers : [];
      return Math.max(maxCount, rowTiers.includes('all') ? 1 : rowTiers.length || 1);
    }, 1);

    if (maxTierCount >= 3) return 260;
    if (maxTierCount === 2) return 190;
    return 130;
  }, [rows]);

  const handleChangeStatus = useCallback(
    async (benefitId: string, newStatus: BenefitStatus) => {
      const previousStatus = rows.find((row) => row.benefitId === benefitId)?.status;
      setStatusByBenefitId((current) => ({ ...current, [benefitId]: newStatus }));

      try {
        const response = await repository.changeBenefitStatus({ benefitId, newStatus });

        if (response.isLeft()) {
          throw response.value;
        }

        enqueueSnackbar('Status do benefício atualizado com sucesso!', { variant: 'success' });
      } catch (error) {
        setStatusByBenefitId((current) => {
          const next = { ...current };
          if (previousStatus === 'active' || previousStatus === 'inactive') {
            next[benefitId] = previousStatus;
          } else {
            delete next[benefitId];
          }
          return next;
        });
        enqueueSnackbar(error?.message || 'Erro ao atualizar o status do benefício.', {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, rows]
  );

  const columns = useMemo(
    () => getBenefitsColumns({ formatDate, formatPoints, handleChangeStatus, tierColumnWidth }),
    [formatDate, formatPoints, handleChangeStatus, tierColumnWidth]
  );

  const selectedFiltersCount = useMemo(() => {
    if (!appliedDraftFilters) return 0;

    return [
      appliedDraftFilters.benefitType.length > 0,
      appliedDraftFilters.tierSort.length > 0,
      Boolean(appliedDraftFilters.status),
      Boolean(appliedDraftFilters.company.trim()),
      Boolean(appliedDraftFilters.startDate || appliedDraftFilters.endDate),
      appliedDraftFilters.categoriesId.length > 0,
      appliedDraftFilters.themesId.length > 0,
      appliedDraftFilters.exhibitionEnv.length > 0,
    ].filter(Boolean).length;
  }, [appliedDraftFilters]);

  const handleSearchTextChange = useCallback((value: string) => {
    setSearchTextState(value);
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, []);

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model[0] || null);
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, []);

  const onApplyFilters = useCallback(
    (payload: BenefitsDrawerApplyPayload) => {
      setFilters(payload.mappedFilters.filter((filter) => filter.field !== fixedFilterField));
      setAppliedDraftFilters({
        ...payload.draftFilters,
        [fixedFilterField]: [],
      });
      setPaginationModel((current) => ({ ...current, page: 0 }));
    },
    [fixedFilterField]
  );

  return {
    entityId,
    benefitsList,
    rows,
    columns,
    isLoading,
    searchText,
    selectedFiltersCount,
    appliedDraftFilters,
    paginationModel,
    gridSortModel: sortModel ? [sortModel] : [],
    openFiltersDrawer,
    setPaginationModel,
    setOpenFiltersDrawer,
    handleSearchTextChange,
    handleSortModelChange,
    onApplyFilters,
  };
}
